import { dateTimeFormat } from '@magicdoorinc/date';
import { Form, FormItem } from '@magicdoorinc/form';
import {
  LabeledDateInputField,
  LabeledDateTimeInput,
  LabeledNumberInput,
  LabeledSelect,
  LabeledTextInput,
  useLocalization,
} from '@magicdoorinc/ui';
import { createEffect, createMemo, createSignal, For, on } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import IconEye from '~/assets/images/magic-tags-icons/eye.svg?component-solid';
import { LabelWithOptional } from '~/components/common';
import ExpandableSection from '~/components/common/expandables/ExpandableSection';
import { Switcher } from '~/components/magic-tags-v2/Switcher';
import { useMagicTags } from '~/contexts/global';
import { CustomFieldComponentType } from '~/swagger/portal/Api';
import { MAX_INPUT_AMOUNT } from '~/utils/constant';
import { commonTagFieldsDict, magicTagTypeDict, SupportedReferenceEntityTypeDict } from './Constant';
import { OptionsInput } from './OptionsInput';
import type { BetterForm, useForm } from '@magicdoorinc/form';
import type { Component, JSX } from 'solid-js';
import type { EntityType } from '~/swagger/portal/Api';

export interface RestFieldsResult {
  requiredFields: () => JSX.Element | null;
  optionalFields: () => JSX.Element | null;
}

const countSelectOptions = (values: unknown): number => {
  if (!Array.isArray(values)) return 0;
  return values.filter((item) => typeof item === 'string' && item.trim()).length;
};

const maxSelectionsExceedsOptions = (maxLength: unknown, values: unknown): boolean => {
  const optionCount = countSelectOptions(values);
  return typeof maxLength === 'number' && optionCount > 0 && maxLength > optionCount;
};

const restFieldsByType = (type: CustomFieldComponentType, form: BetterForm.form): RestFieldsResult => {
  const { t } = useLocalization();
  const typesMap: Record<CustomFieldComponentType, RestFieldsResult> = {
    [CustomFieldComponentType.Number]: {
      requiredFields: () => null,
      optionalFields: () => (
        <>
          <FormItem component={LabeledNumberInput} label={t('Min')} formFieldName={['options', 'min']} placeholder={t('Please enter')} />
          <FormItem
            component={LabeledNumberInput}
            rules={[
              {
                validator(value) {
                  if (value && value < form.formStore.options.min) {
                    return t('Max must be greater than min');
                  }

                  return true;
                },
              },
            ]}
            label={t('Max')}
            formFieldName={['options', 'max']}
            placeholder={t('Please enter')}
          />
          <FormItem
            component={LabeledNumberInput}
            label={t('Step')}
            rules={[
              {
                range: [0, MAX_INPUT_AMOUNT],
                type: 'number',
                message: t('Step must be a number'),
              },
            ]}
            formFieldName={['options', 'step']}
            placeholder={t('Please enter')}
          />
          <FormItem
            component={LabeledNumberInput}
            label={t('Default value')}
            formFieldName={['options', 'default']}
            placeholder={t('Please enter')}
          />
        </>
      ),
    },
    [CustomFieldComponentType.Input]: {
      requiredFields: () => null,
      optionalFields: () => (
        <>
          <FormItem
            component={LabeledNumberInput}
            rules={[
              {
                length: [1, MAX_INPUT_AMOUNT],
                message: t('{name} must be between {min} and {max} characters', {
                  name: t('Min length'),
                  min: '1',
                  max: MAX_INPUT_AMOUNT,
                }),
              },
            ]}
            label={t('Min length')}
            formFieldName={['options', 'minLength']}
            placeholder={t('Please enter')}
          />
          <FormItem
            component={LabeledNumberInput}
            label={t('Max length')}
            rules={[
              {
                length: [1, MAX_INPUT_AMOUNT],
                message: t('{name} must be between {min} and {max} characters', {
                  name: t('Max length'),
                  min: '1',
                  max: MAX_INPUT_AMOUNT,
                }),
              },
              {
                validator(value) {
                  if (value && value <= form.formStore.options.minLength) {
                    return t('Max length must be greater than min length');
                  }

                  return true;
                },
              },
            ]}
            formFieldName={['options', 'maxLength']}
            placeholder={t('Please enter')}
          />
          <FormItem
            component={LabeledTextInput}
            label={t('Default value')}
            formFieldName={['options', 'default']}
            placeholder={t('Please enter')}
          />
        </>
      ),
    },
    [CustomFieldComponentType.Textarea]: {
      requiredFields: () => null,
      optionalFields: () => (
        <>
          <FormItem
            component={LabeledNumberInput}
            label={t('Min length')}
            formFieldName={['options', 'minLength']}
            placeholder={t('Please enter')}
          />
          <FormItem
            component={LabeledNumberInput}
            label={t('Max length')}
            formFieldName={['options', 'maxLength']}
            placeholder={t('Please enter')}
          />
          <FormItem
            component={LabeledTextInput}
            label={t('Default value')}
            formFieldName={['options', 'default']}
            placeholder={t('Please enter')}
          />
        </>
      ),
    },
    [CustomFieldComponentType.Select]: {
      requiredFields: () => (
        <div class="col-span-full grid grid-cols-1">
          <FormItem
            component={OptionsInput}
            label={t('Values')}
            formFieldName={['options', 'values']}
            rules={[
              { required: true, message: t('{name} is required', { name: t('Values') }) },
              {
                validator(value) {
                  if (value && value.some((item: string) => !item.trim())) {
                    return t('Option cannot be empty');
                  }

                  if (maxSelectionsExceedsOptions(form.formStore.options.maxLength, value)) {
                    return t('Max selections cannot exceed the number of options');
                  }

                  return true;
                },
              },
            ]}
          />
        </div>
      ),
      optionalFields: () => (
        <>
          <FormItem
            component={LabeledNumberInput}
            label={t('Max selections')}
            formFieldName={['options', 'maxLength']}
            placeholder={t('Please enter')}
            min={1}
            rules={[
              {
                type: 'int',
                range: [1, MAX_INPUT_AMOUNT],
                message: t('{name} must be between {min} and {max}', {
                  name: t('Max selections'),
                  min: '1',
                  max: String(MAX_INPUT_AMOUNT),
                }),
              },
              {
                validator(value) {
                  if (maxSelectionsExceedsOptions(value, form.formStore.options.values)) {
                    return t('Max selections cannot exceed the number of options');
                  }

                  if (value && form.formStore.options.minLength && value < form.formStore.options.minLength) {
                    return t('Max selections cannot be less than min selections');
                  }

                  return true;
                },
              },
            ]}
          />
          <FormItem
            component={LabeledTextInput}
            label={t('Default value')}
            formFieldName={['options', 'default']}
            placeholder={t('Please enter')}
          />
        </>
      ),
    },
    [CustomFieldComponentType.File]: {
      requiredFields: () => (
        <FormItem
          component={LabeledSelect}
          options={SupportedReferenceEntityTypeDict.getOptions()}
          rules={[{ required: true, message: t('{name} is required', { name: t('Reference entity type') }) }]}
          label={t('Reference entity type')}
          formFieldName={['options', 'referenceEntityType']}
          placeholder={t('Please select')}
        />
      ),
      optionalFields: () => (
        <FormItem
          component={LabeledSelect}
          label={t('Multiple')}
          formFieldName={['options', 'multiple']}
          options={[
            { label: t('Yes'), value: true },
            { label: t('No'), value: false },
          ]}
          placeholder={t('Please select')}
        />
      ),
    },
    [CustomFieldComponentType.Checkbox]: {
      requiredFields: () => null,
      optionalFields: () => (
        <FormItem
          component={LabeledSelect}
          label={t('Default value')}
          formFieldName={['options', 'default']}
          options={[
            { label: t('Yes'), value: true },
            { label: t('No'), value: false },
          ]}
          placeholder={t('Please select')}
        />
      ),
    },
    [CustomFieldComponentType.Date]: {
      requiredFields: () => null,
      optionalFields: () => (
        <>
          <FormItem
            component={LabeledDateInputField}
            label={t('Min')}
            formFieldName={['options', 'min']}
            placeholder={t('Please select')}
          />
          <FormItem
            component={LabeledDateInputField}
            label={t('Max')}
            rules={[
              {
                validator(value) {
                  if (value && form.formStore.options.min && value <= form.formStore.options.min) {
                    return t('Date is invalid, must be greater than {min}', { min: dateTimeFormat(form.formStore.options.min) });
                  }

                  return true;
                },
              },
            ]}
            formFieldName={['options', 'max']}
            placeholder={t('Please select')}
          />
          <FormItem
            component={LabeledDateInputField}
            label={t('Default value')}
            formFieldName={['options', 'default']}
            placeholder={t('Please select')}
          />
        </>
      ),
    },
    [CustomFieldComponentType.DateTime]: {
      requiredFields: () => null,
      optionalFields: () => (
        <>
          <FormItem component={LabeledDateTimeInput} label={t('Min')} formFieldName={['options', 'min']} />
          <FormItem
            component={LabeledDateTimeInput}
            label={t('Max')}
            formFieldName={['options', 'max']}
            rules={[
              {
                validator(value) {
                  if (value && form.formStore.options.min && value <= form.formStore.options.min) {
                    return t('Date is invalid, must be greater than {min}', { min: dateTimeFormat(form.formStore.options.min) });
                  }

                  return true;
                },
              },
            ]}
          />
          <FormItem
            component={LabeledDateTimeInput}
            label={t('Default value')}
            formFieldName={['options', 'default']}
            rules={[
              {
                validator(value) {
                  if (
                    value &&
                    ((form.formStore.options.min && value <= form.formStore.options.min) ||
                      (form.formStore.options.max && value >= form.formStore.options.max))
                  ) {
                    return t('Date is invalid, must be between {min} and {max}', {
                      min: dateTimeFormat(form.formStore.options.min),
                      max: dateTimeFormat(form.formStore.options.max),
                    });
                  }

                  return true;
                },
              },
            ]}
          />
        </>
      ),
    },
  };

  return typesMap[type];
};

export interface AddOrEditTagFormBaseProps {
  form: ReturnType<typeof useForm>;
  entityType: EntityType;
  isEdit?: boolean;
}

export const AddOrEditTagFormBase: Component<AddOrEditTagFormBaseProps> = (props) => {
  const { t } = useLocalization();
  const { customField } = useMagicTags();
  const [isExpandableOpen, setIsExpandableOpen] = createSignal(true);
  const isEdit = createMemo(() => props.isEdit ?? false);
  const restFields = createMemo(() => {
    const tagType = props.form.formStore.magicTagType;
    return restFieldsByType(tagType, props.form);
  });

  createEffect(() => {
    const _customField = customField();

    if (isEdit() && _customField) {
      props.form.setFormStore({
        name: _customField.name,
        magicTagType: _customField.options.componentType,
        listable: true,
        filterable: _customField.filterable,
        public: _customField.public,
        options: _customField.options,
        description: _customField.description,
      });
    }
  });

  const isFilterableDisabled = createMemo(() => {
    return props.form.formStore.magicTagType === CustomFieldComponentType.File;
  });

  createEffect(() => {
    if (isFilterableDisabled()) {
      props.form.setFormStore('filterable', false);
    }
  });

  createEffect(
    on(
      () => [props.form.formStore.magicTagType, props.form.formStore.options?.values] as const,
      ([tagType]) => {
        if (tagType !== CustomFieldComponentType.Select) return;
        if (typeof props.form.formStore.options?.maxLength !== 'number') return;
        props.form.validateForm(['options', 'maxLength']);
      },
      { defer: true }
    )
  );

  return (
    <Form defaultForm={props.form} class="grid grid-cols-1 gap-x-4 gap-y-5 px-5 py-4 lg:grid-cols-2">
      <FormItem
        component={LabeledSelect<CustomFieldComponentType>}
        label={t('Type')}
        rules={[{ required: true, message: t('{name} is required', { name: t('Type') }) }]}
        formFieldName={'magicTagType'}
        options={magicTagTypeDict.getOptions()}
        disabled={isEdit()}
        placeholder={t('Please select')}
        renderLabel={(op) => {
          const items = Array.isArray(op) ? op : [op];
          return (
            <For each={items}>
              {(item) => {
                if (item.type === 'group') return null;
                return (
                  <div class="flex items-center gap-2">
                    <Dynamic class="size-6" component={magicTagTypeDict.getMetaByValue(item.value)?.icon} />
                    {item.label}
                  </div>
                );
              }}
            </For>
          );
        }}
      />

      <FormItem
        component={LabeledTextInput}
        label={t('Name')}
        formFieldName={'name'}
        rules={[
          { required: true, message: t('{name} is required', { name: t('Name') }) },
          {
            length: [1, 100],
            message: t('{name} must be between {min} and {max} characters', { name: t('Name'), min: '1', max: '100' }),
          },
        ]}
        placeholder={t('Please enter')}
      />

      <Dynamic component={restFields().requiredFields} />

      <div class="col-span-full grid grid-cols-1 gap-x-4 gap-y-5 lg:grid-cols-2">
        <FormItem
          component={Switcher}
          label={t('Filterable tag')}
          formFieldName={'filterable'}
          icon={IconEye}
          disabled={isFilterableDisabled()}
        />

        <FormItem
          component={Switcher}
          label={t(commonTagFieldsDict.getLabelByValue('public'))}
          formFieldName={'public'}
          icon={commonTagFieldsDict.getMetaByValue('public')!.icon}
        />

        <FormItem
          component={Switcher}
          label={t(commonTagFieldsDict.getLabelByValue('options.required'))}
          formFieldName={['options', 'required']}
          icon={commonTagFieldsDict.getMetaByValue('options.required')!.icon}
        />
      </div>

      <ExpandableSection
        open={isExpandableOpen()}
        onOpenChange={setIsExpandableOpen}
        headerBorder
        class="col-span-full rounded-lg border border-separator px-0 text-base"
        triggerTitle={<LabelWithOptional class="pl-4" label={t('Advanced information')} />}>
        <div class="grid grid-cols-1 gap-x-4 gap-y-5 px-4 pb-4 lg:grid-cols-2">
          <FormItem
            component={LabeledTextInput}
            label={t('Description')}
            formFieldName={'description'}
            rules={[
              {
                length: [0, 500],
                message: t('{name} must be between {min} and {max} characters', {
                  name: t('Description'),
                  min: '0',
                  max: '500',
                }),
              },
            ]}
            placeholder={t('Please enter')}
          />
          <Dynamic component={restFields().optionalFields} />
        </div>
      </ExpandableSection>
    </Form>
  );
};

export const buildSubmitPayload = (formStore: any, entityType: EntityType) => {
  const metaInfo = magicTagTypeDict.getMetaByValue(formStore.magicTagType);

  const { options, ...rest } = formStore;

  delete rest.magicTagType;

  return {
    name: formStore.name,
    options: {
      ...metaInfo?.optionsDefault,
      ...options,
    },
    entityType,
    listable: true,
    ...(rest as any),
  };
};
